/**
 * The empty state a non-plannable scope gets instead of a zero-capacity run
 * (PRD Part C §6.1).
 *
 * Binding a list of people or risks to a scenario is not a mistake and not an
 * error — it is a category the engine does not read. Say that, name what the
 * engine does read, and say what to do if those priorities really do carry
 * resource commitments.
 */

import { Compass } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { NON_PLANNABLE_EMPTY_STATE } from '../../../lib/planningScopePlannability';
import { engineInputKindLabel } from '../../../lib/planningEngineService';

/** True when the active scope has been measured and the engine cannot read it. */
// eslint-disable-next-line react-refresh/only-export-components -- one notice + its predicate; splitting a two-line hook into its own module buys nothing
export function useScopeIsNotPlannable(): boolean {
  const { scopePlannability } = usePlanningEngine();
  return scopePlannability != null && !scopePlannability.plannable;
}

export default function NonPlannableScopeNotice() {
  const { scopePlannability, engineInput } = usePlanningEngine();
  if (!scopePlannability || scopePlannability.plannable) return null;

  const scopeLabel =
    engineInput.type === 'live'
      ? 'All Live Items'
      : `${engineInputKindLabel(engineInput.type)}: ${engineInput.name}`;

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-8 md:p-10 text-center">
      <Compass className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
      <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
        {scopeLabel} is not work to resource
      </p>
      <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] max-w-xl mx-auto">
        {NON_PLANNABLE_EMPTY_STATE}
      </p>
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-3">
        {scopePlannability.reason}
      </p>
    </div>
  );
}
